import type {
  CreatePartner,
  Partner,
  PartnerWithExpenseSplitsResponse,
  UpdatePartner,
} from "@home-finance-manager/shared/types/partners"
import { BaseSDK } from "./base-sdk"

export class PartnersSDK extends BaseSDK {
  /**
   * Get all partners for the authenticated user
   * @returns Array of partners
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async getPartners(): Promise<Partner[]> {
    const response = await this.fetchWithErrorHandling<{ data: Partner[] }>(
      `${this.baseUrl}/partners`,
      {
        headers: this.getAuthHeaders(),
      }
    )
    return this.extractData<Partner[]>(response) || []
  }

  /**
   * Get a specific partner by ID
   * @param id - The partner ID
   * @returns The partner
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async getPartner(id: number): Promise<Partner> {
    const response = await this.fetchWithErrorHandling<{ data: Partner }>(
      `${this.baseUrl}/partners/${id}`,
      {
        headers: this.getAuthHeaders(),
      }
    )
    return this.extractData<Partner>(response)
  }

  /**
   * Get a partner with their expense splits
   * @param id - The partner ID
   * @returns The partner with expense splits
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async getPartnerWithExpenseSplits(id: number): Promise<PartnerWithExpenseSplitsResponse> {
    const response = await this.fetchWithErrorHandling<{
      data: PartnerWithExpenseSplitsResponse
    }>(`${this.baseUrl}/partners/${id}/expense-splits`, {
      headers: this.getAuthHeaders(),
    })
    return this.extractData<PartnerWithExpenseSplitsResponse>(response)
  }

  /**
   * Update a partner
   * @param id - The partner ID
   * @param updateData - The partner data to update
   * @returns The updated partner
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async updatePartner(id: number, updateData: UpdatePartner): Promise<Partner> {
    const response = await this.fetchWithErrorHandling<{ data: Partner }>(
      `${this.baseUrl}/partners/${id}`,
      {
        method: "PUT",
        headers: this.getAuthHeaders(),
        body: JSON.stringify(updateData),
      }
    )
    return this.extractData<Partner>(response)
  }

  /**
   * Delete a partner
   * @param id - The partner ID
   * @returns Success message
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async deletePartner(id: number): Promise<void> {
    await this.fetchWithErrorHandling(`${this.baseUrl}/partners/${id}`, {
      method: "DELETE",
      headers: this.getAuthHeaders(),
    })
  }

  /**
   * Create a new partner
   * @param partnerData - The partner data
   * @returns The created partner
   * @throws {AuthError} If authentication/authorization fails (401, 403)
   * @throws {ClientError} If request is invalid (400-499, excluding 401, 403)
   * @throws {ServerError} If server error occurs (500-599)
   * @throws {NetworkError} If network request fails
   */
  async createPartner(partnerData: CreatePartner): Promise<Partner> {
    const response = await this.fetchWithErrorHandling<{ data: Partner }>(
      `${this.baseUrl}/partners`,
      {
        method: "POST",
        headers: this.getAuthHeaders(),
        body: JSON.stringify(partnerData),
      }
    )
    return this.extractData<Partner>(response)
  }
}
